import { useCallback, useEffect, useMemo, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import debounce from 'lodash.debounce'

import { QueryVariables } from './usePaginatedGuests'

export default function useGuestTable(selectedIds: number[]) {
  const [selectedGuests, setSelectedGuests] = useState<number[]>(selectedIds)
  const [search, setSearch] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<GraphQL.SortDirection>(
    GraphQL.SortDirection.Asc
  )
  const [sortField, setSortField] = useState<GraphQL.GuestSortField>(
    GraphQL.GuestSortField.FirstName
  )

  const [variables, setVariables] = useState<QueryVariables>({
    filter: {
      search
    },
    sort: {
      direction: sortDirection,
      field: sortField
    }
  })

  const updateSort = useCallback(
    (field: GraphQL.GuestSortField, direction: GraphQL.SortDirection) => {
      setSortField(field)
      setSortDirection(direction)
    },
    [setSortField, setSortDirection]
  )

  const debounceSearch = useMemo(() => {
    return debounce(
      (search: string) =>
        setVariables(vars => ({
          ...vars,
          filter: {
            ...vars.filter,
            search
          }
        })),
      1000
    )
  }, [setVariables])

  useEffect(() => {
    if (variables.filter.search === search) return

    debounceSearch(search)
  }, [variables, search, debounceSearch])

  useEffect(() => {
    setVariables(variables => ({
      ...variables,
      sort: {
        direction: sortDirection,
        field: sortField
      }
    }))
  }, [sortDirection, sortField, setVariables])

  const onSelectGuest = useCallback(
    (guest: GraphQL.GuestFragmentFragment) => {
      setSelectedGuests(ids => {
        const newIds = ids.filter(id => id !== guest.id)

        if (newIds.length === ids.length) return [...ids, guest.id]
        else return newIds
      })
    },
    [setSelectedGuests]
  )

  return {
    queryVariables: variables,
    selectedGuests,
    search,
    onSelectGuest,
    setSearch,
    updateSort
  }
}
